import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./Public.css";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import useSeo from "../../hooks/useSeo";

const pubUrl = process.env.PUBLIC_URL || "";

export default function Commercial() {
  useSeo({
    title: "Commercial Roofing | Crowned Roofing (Toronto, ON)",
    description:
      "Commercial roofing services in Toronto and surrounding Ontario markets: targeted repairs, replacements, and scope development based on real inspection findings and clear documentation.",
    ogImage: "https://www.crownedroofing.com/images/ogimg.png",
    canonical: "https://www.crownedroofing.com/commercial",
  });

  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Commercial Roofing | Crowned Roofing";
    const meta = document.querySelector("meta[name='description']");
    if (meta) {
      meta.setAttribute(
        "content",
        "Inspection-led commercial roofing services built around documentation, clear scopes, and practical planning for repairs, maintenance, and replacement."
      );
    }
  }, []);

  return (
    <main className="publicPage">
      <Header />

      <section className="cHero">
        <div
          className="cHeroBg"
          aria-hidden="true"
          style={{ backgroundImage: `url(${pubUrl}/images/commercial-hero.png)` }}
        />
        <div className="cHeroInner">
          <div className="cHeroPanel">
            <h1>Commercial Roofing, Built on Inspection &amp; Documentation</h1>
            <p>
              Inspection-driven commercial roofing services delivering clear documentation,
              condition-based recommendations, and realistic scopes for repair, maintenance,
              and replacement planning.
            </p>
            <button className="cBtn" onClick={() => navigate("/contact")}>
              REQUEST AN ASSESSMENT
            </button>
          </div>
        </div>
      </section>

      <section className="cTileRow">
        <div className="cTileGrid">
          <div className="cTile">
            <img src={`${pubUrl}/icons/home-banner-ico1.png`} alt="" aria-hidden="true" />
            <div className="cTileTitle">Commercial &amp; Industrial Focus</div>
          </div>
          <div className="cTile">
            <img src={`${pubUrl}/icons/home-banner-ico2.png`} alt="" aria-hidden="true" />
            <div className="cTileTitle">Inspection-Led Roofing Decisions</div>
          </div>
          <div className="cTile">
            <img src={`${pubUrl}/icons/home-banner-ico3.png`} alt="" aria-hidden="true" />
            <div className="cTileTitle">Clear Reporting &amp; Documentation</div>
          </div>
          <div className="cTile">
            <img src={`${pubUrl}/icons/home-banner-ico4.png`} alt="" aria-hidden="true" />
            <div className="cTileTitle">Repair, Maintenance, &amp; Replacement Planning</div>
          </div>
        </div>
      </section>

      <section className="cSection cProblem">
        <div className="cProblemGrid">
          <div className="cProblemText">
            <h2>
              <span className="cAccent" aria-hidden="true" />
              Most roofing decisions start too late.
            </h2>

            <p>
              Many commercial and industrial roofs are evaluated only when a leak becomes disruptive
              or when replacement is already being pushed as the default solution. At that point,
              owners and facility teams are forced to make high-cost decisions quickly, often without
              clear documentation or a full understanding of the roof condition.
            </p>

            <p>
              Inspections are rushed. Recommendations are vague. Scopes are oversized or incomplete,
              and critical details such as remaining service life, repair viability, or long-term
              planning are frequently overlooked.
            </p>

            <div className="cProblemMini">
              <div className="cMiniTitle">The result is uncertainty:</div>
              <ul className="cBulletList">
                <li>Repairs that do not fully resolve the issue</li>
                <li>Replacements that may not have been necessary yet</li>
                <li>Budgets based on assumptions rather than documented conditions</li>
              </ul>
              <div className="cMiniNote">
                <strong>Roofing should start with clarity,</strong> not pressure.
              </div>
            </div>

            <div className="cSmallNote">
              That is where the process often breaks down, and where Crowned Roofing takes a different approach.
            </div>
          </div>

          <div className="cProblemVisual" aria-hidden="true">
            <img src={`${pubUrl}/images/commercial-img1.png`} alt="Contractor on commercial roof" />
          </div>
        </div>
      </section>

      <section className="cSection cProcess">
        <div className="cSectionHead">
          <h2>
            <span className="cAccent" aria-hidden="true" />
            An Inspection-Led Roofing Process
          </h2>
          <div className="cSectionSub">Every project starts with understanding the roof as it exists today.</div>
        </div>

        <div className="cProcessGrid">
          <div className="cStep">
            <div className="cStepNum">01</div>
            <div className="cStepTitle">01 - Inspect</div>
            <div className="cStepBody">
              We evaluate the roof system as it exists today, identifying active issues, system condition,
              and areas that require closer attention.
            </div>
          </div>

          <div className="cStep">
            <div className="cStepNum">02</div>
            <div className="cStepTitle">02 - Document</div>
            <div className="cStepBody">
              Findings are supported with clear photos and written observations that can be reviewed,
              shared, and referenced over time.
            </div>
          </div>

          <div className="cStep">
            <div className="cStepNum">03</div>
            <div className="cStepTitle">03 - Plan</div>
            <div className="cStepBody">
              Recommendations are based on documented conditions, whether that means targeted repairs,
              maintenance planning, or future replacement.
            </div>
          </div>
        </div>
      </section>

      <section className="cSection cDifferent">
        <div className="cSectionHead">
          <h2>
            <span className="cAccent" aria-hidden="true" />
            How We&apos;re Different
          </h2>
          <div className="cSectionSub">Our approach is not typical, and that matters in commercial roofing.</div>
        </div>

        <div className="cCompare">
          <div className="cCompareCol">
            <div className="cCompareHead">Traditional Roofing</div>
            <ul className="cCompareList">
              <li>Reactive to leaks and visible failures</li>
              <li>Sales-driven recommendations</li>
              <li>Replacement positioned as the default solution</li>
              <li>Limited or inconsistent inspection documentation</li>
              <li>Scopes based on assumptions or averages</li>
              <li>Focused on project size rather than necessity</li>
              <li>Minimal discussion of remaining service life</li>
              <li>Repairs treated as temporary stopgaps</li>
              <li>Little consideration for long-term planning</li>
              <li>Decisions made under time pressure</li>
            </ul>
          </div>

          <div className="cCompareCol crowned">
            <div className="cCompareHead crowned">Crowned Roofing</div>
            <ul className="cCompareList crowned">
              <li>Inspection-led from the start</li>
              <li>Condition-driven recommendations</li>
              <li>Repairs, maintenance, or replacement based on need</li>
              <li>Clear photo documentation and written findings</li>
              <li>Scopes tied directly to observed conditions</li>
              <li>Focused on system performance and risk</li>
              <li>Service life and repair viability clearly evaluated</li>
              <li>Repairs used strategically where appropriate</li>
              <li>Emphasis on planning and predictability</li>
              <li>Decisions supported with clarity, not pressure</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="cFinal">
        <div className="cFinalCard">
          <div className="cFinalTitle">Start With an Inspection</div>
          <div className="cFinalSub">
            If you need clarity on the condition of your roof, we can help you understand your options before any scope is discussed.
          </div>
          <button className="cBtn" onClick={() => navigate("/contact")}>
            REQUEST A ROOF ASSESSMENT
          </button>
          <button className="cLink" onClick={() => navigate("/inspections")}>
            View a Sample Inspection Report
          </button>
          <button className="cLink" onClick={() => navigate("/faq/cost-of-new-roof-ontario")}>
            Read the FAQ on Ontario roof costs
          </button>
        </div>
      </section>

      <Footer />
    </main>
  );
}
